import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  Injector,
  ElementRef,
  ViewChild,
  HostListener,
  OnDestroy,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RoleService } from '@app/shared/services/roles.service';
import { TerminologyRefreshSignalService } from '@app/shared/services/terminology-refresh-signal.service';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import {
  catchError,
  firstValueFrom,
  forkJoin,
  Subscription,
  throwError,
} from 'rxjs';
import { DocumentAssetsService } from '../document-assets/document-assets.service';
import {
  sameAssetReference,
  sameBrandReference,
  type DocumentAssetCatalog,
  type DocumentBrandVersion,
} from '../document-assets/document-assets.models';
import { WorkflowDraftConfirmationService } from '../workflow-registry-diagnostics/workflow-draft-confirmation.service';
import type {
  NoticeBlock,
  NoticeCatalog,
  NoticeComposition,
  NoticeContextField,
  NoticeContextList,
  NoticeDiagnostic,
  NoticeProof,
  NoticeScope,
  SavedNoticeSource,
} from './notice-fidelity.models';
import { NoticeFidelityService } from './notice-fidelity.service';
import { NoticeFidelityStateService } from './notice-fidelity-state.service';
import { NoticeDiagnosticsComponent } from './notice-diagnostics.component';
import {
  resolveDiagnosticTarget,
  type NoticeDiagnosticNavigation,
  type NoticeDiagnosticTarget,
} from './notice-diagnostic-navigation';
import { NoticeCompositionHistory } from './notice-composition-history';
import { NoticeProofViewerComponent } from './notice-proof-viewer.component';
import { NoticeChangeReviewComponent } from './notice-change-review.component';
import {
  compareNoticeCompositions,
  type NoticeCompositionComparison,
} from './notice-composition-diff';
import { matchNoticeReferenceEvidence } from './notice-reference-evidence';
import { matchNoticeTerminologyEvidence } from './notice-terminology-evidence';
import {
  applyNoticeReferenceCommand,
  noticeReferenceSelections,
  type NoticeReferenceCommand,
  type NoticeReferenceSelection,
} from './notice-reference-commands';
import { NoticeDocumentSettingsComponent } from './notice-document-settings.component';
import {
  applyNoticeDocumentSetting,
  type NoticeDocumentSetting,
} from './notice-document-settings';
import {
  applyNoticeStructureCommand,
  type NoticeStructureCommand,
} from './notice-structure-commands';
import {
  NoticePageEditorComponent,
  type NoticePageChange,
  type NoticePageViewState,
  type NoticePageViewStateChange,
} from './notice-page-editor.component';

class NoticeComparisonFailure {
  constructor(
    readonly side: 'baseline' | 'current',
    readonly error: unknown,
  ) {}
}

@Component({
  selector: 'phms-platform-notice-fidelity',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    EzStandardStateComponent,
    NoticePageEditorComponent,
    NoticeDocumentSettingsComponent,
    NoticeChangeReviewComponent,
    NoticeProofViewerComponent,
    NoticeDiagnosticsComponent,
  ],
  templateUrl: './platform-notice-fidelity.component.html',
  styleUrl: './platform-notice-fidelity.component.scss',
})
export class PlatformNoticeFidelityComponent implements OnDestroy {
  private readonly api = inject(NoticeFidelityService);
  private readonly injector = inject(Injector);
  assetCatalog: DocumentAssetCatalog | null = null;
  assetsLoading = false;
  assetsError = '';
  brandProfile: DocumentBrandVersion | null = null;
  brandLogoUrl = '';
  brandError = '';
  private assetGeneration = 0;
  private brandGeneration = 0;
  private brandIdentity = '';
  private get assetsApi(): DocumentAssetsService {
    return this.injector.get(DocumentAssetsService);
  }
  async loadAssets(): Promise<void> {
    const scope = this.scope;
    if (
      !scope ||
      !this.canRead() ||
      this.assetsLoading ||
      this.destroyed ||
      this.accessLost
    )
      return;
    const generation = ++this.assetGeneration,
      scopeKey = this.scopeKey,
      actorId = this.actorId;
    this.assetsLoading = true;
    this.assetsError = '';
    try {
      const catalog = await firstValueFrom(this.assetsApi.catalog(scope));
      if (
        this.destroyed ||
        generation !== this.assetGeneration ||
        scopeKey !== this.scopeKey ||
        actorId !== this.actorId ||
        !this.canRead() ||
        this.accessLost
      )
        return;
      this.assetCatalog = catalog;
      this.brandIdentity = '';
      this.syncBrand();
    } catch (error) {
      if (this.destroyed || generation !== this.assetGeneration) return;
      if (
        error instanceof HttpErrorResponse &&
        [401, 403].includes(error.status)
      ) {
        this.handleError(error);
        return;
      }
      this.assetsError =
        'The reusable asset library could not be loaded. Your source is preserved; refresh profiles to retry.';
      this.brandError = this.composition?.branding.profile
        ? this.assetsError
        : '';
    } finally {
      if (generation === this.assetGeneration) this.assetsLoading = false;
    }
  }
  private clearAssets(): void {
    this.assetGeneration++;
    this.assetsLoading = false;
    this.assetCatalog = null;
    this.assetsError = '';
    this.brandGeneration++;
    this.brandIdentity = '';
    this.brandProfile = null;
    this.brandError = '';
    this.releaseBrandLogo();
  }
  private releaseBrandLogo(): void {
    if (this.brandLogoUrl) URL.revokeObjectURL(this.brandLogoUrl);
    this.brandLogoUrl = '';
  }
  private syncBrand(): void {
    const reference = this.composition?.branding.profile;
    const identity = JSON.stringify([this.scopeKey, reference ?? null]);
    if (identity === this.brandIdentity) return;
    this.brandIdentity = identity;
    this.brandGeneration++;
    this.brandProfile = null;
    this.brandError = '';
    this.releaseBrandLogo();
    if (!reference) return;
    if (!this.assetCatalog) {
      this.brandError = 'Loading the retained brand profile and exact assets…';
      if (!this.assetsLoading && !this.assetsError) void this.loadAssets();
      return;
    }
    const brand = this.assetCatalog.brands.find((item) =>
      sameBrandReference(item.reference, reference),
    );
    const font =
      brand &&
      this.assetCatalog.fonts.find(
        (item) =>
          item.code === brand.style.fontCode &&
          item.sha256 === brand.style.fontSha256 &&
          item.available,
      );
    if (
      !brand ||
      !font ||
      !this.catalog?.fonts.some(
        (item) => item.code === font.code && item.sha256 === font.sha256,
      )
    ) {
      this.brandError =
        'The retained brand profile or its exact supported font is unavailable. Its identity is preserved; refresh profiles or remove it in document settings.';
      return;
    }
    this.brandProfile = brand;
    if (!brand.style.logo) return;
    const logo = this.assetCatalog.items.find((item) =>
      sameAssetReference(item.reference, brand.style.logo),
    );
    if (!logo || !['image/png', 'image/jpeg'].includes(logo.contentType)) {
      this.brandError =
        'The exact retained logo is unavailable. Your source is preserved.';
      return;
    }
    const generation = this.brandGeneration,
      scopeKey = this.scopeKey,
      actorId = this.actorId;
    this.brandError = 'Verifying the exact retained logo…';
    void firstValueFrom(this.assetsApi.content(logo.reference))
      .then(async (bytes) => {
        if (
          !(bytes instanceof ArrayBuffer) ||
          bytes.byteLength !== logo.length ||
          bytes.byteLength <= 0 ||
          bytes.byteLength > 2097152
        )
          throw new Error('Invalid logo bytes.');
        const digest = await crypto.subtle.digest('SHA-256', bytes);
        const hash = [...new Uint8Array(digest)]
          .map((value) => value.toString(16).padStart(2, '0'))
          .join('');
        if (hash !== logo.reference.sha256)
          throw new Error('Logo verification failed.');
        if (
          this.destroyed ||
          generation !== this.brandGeneration ||
          scopeKey !== this.scopeKey ||
          actorId !== this.actorId ||
          !this.canRead() ||
          this.accessLost
        )
          return;
        this.brandLogoUrl = URL.createObjectURL(
          new Blob([bytes], { type: logo.contentType }),
        );
        this.brandError = '';
      })
      .catch((error) => {
        if (this.destroyed || generation !== this.brandGeneration) return;
        if (
          error instanceof HttpErrorResponse &&
          [401, 403].includes(error.status)
        ) {
          this.handleError(error);
          return;
        }
        this.brandError =
          'The exact logo could not be verified. Your source is preserved; refresh profiles to retry.';
      });
  }
  private readonly roles = inject(RoleService);
  private readonly terminologyRefresh = inject(TerminologyRefreshSignalService);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  readonly state = inject(NoticeFidelityStateService);
  readonly canRead = this.roles.hasAnyPermissionSignal('content_template.read');
  catalog: NoticeCatalog | null = null;
  scopeKey = '';
  exampleCode = 'short';
  sourceJson = '';
  composition: NoticeComposition | null = null;
  saved: SavedNoticeSource | null = null;
  proof: NoticeProof | null = null;
  // Captured evidence is deliberately session-only; local source storage is unchanged.
  baseline: SavedNoticeSource | null = null;
  comparisonProofs: { baseline: NoticeProof; current: NoticeProof } | null =
    null;
  diagnostics: NoticeDiagnostic[] = [];
  baselineDiagnostics: NoticeDiagnostic[] = [];
  error = '';
  notice = '';
  loading = false;
  accessLost = false;
  view: 'page' | 'fields' | 'preview' | 'review' = 'page';
  selectedBlockId: string | null = null;
  pageFocusBlockOnOpen: string | null = null;
  pageIssueFocus: NoticeDiagnosticTarget | null = null;
  pageViewIdentity = 0;
  pageViewState: NoticePageViewState | null = null;
  readonly history = new NoticeCompositionHistory();
  @ViewChild('moreActions')
  private moreActions?: ElementRef<HTMLDetailsElement>;
  @ViewChild('moreSummary') private moreSummary?: ElementRef<HTMLElement>;
  @ViewChild('sourceDetails')
  private sourceDetails?: ElementRef<HTMLDetailsElement>;
  @ViewChild('sourceInput')
  private sourceInput?: ElementRef<HTMLTextAreaElement>;
  private contractMismatch = false;
  private generation = 0;
  private catalogGeneration = 0;
  private request?: Subscription;
  private catalogRequest?: Subscription;
  private destroyed = false;
  private actorId = '';
  private untouchedSource: string | null = null;
  private confirmingSave = false;
  private baselineRecovery: {
    previous: SavedNoticeSource;
    expected: SavedNoticeSource | null;
    documentIdentity: number;
  } | null = null;
  constructor() {
    effect(() => {
      this.terminologyRefresh.counter();
      untracked(() => {
        if (
          this.proof?.terminologySnapshot ||
          this.comparisonProofs?.baseline.terminologySnapshot
        )
          this.invalidateTerminologyProof(
            this.terminologyRefresh.status() === 'unavailable',
          );
      });
    });
    effect(() => {
      const allowed = this.canRead();
      const actor = this.roles.accessProfile()?.actor.actorId ?? '';
      untracked(() => {
        if (!allowed || !actor) {
          this.clearProtected();
          return;
        }
        if (this.actorId && this.actorId !== actor) this.clearProtected();
        this.actorId = actor;
        this.loadCatalog();
      });
    });
  }
  get scope(): NoticeScope | null {
    return (
      this.catalog?.scopes.find((s) => this.scopeValue(s) === this.scopeKey) ??
      null
    );
  }
  scopeValue(scope: NoticeScope): string {
    return JSON.stringify([scope.scopeTypeCode, scope.scopeKey]);
  }
  get busy(): boolean {
    return this.state.busy();
  }
  get dirty(): boolean {
    return (
      this.sourceJson !== (this.saved?.sourceJson ?? this.untouchedSource ?? '')
    );
  }
  get canEdit(): boolean {
    return (
      this.canRead() &&
      !this.contractMismatch &&
      !!this.scope &&
      !this.accessLost &&
      !this.busy &&
      !this.loading &&
      this.roles.accessProfile()?.actor.actorId === this.actorId
    );
  }
  get canSave(): boolean {
    return (
      this.canEdit &&
      !this.confirmingSave &&
      !!this.sourceJson.trim() &&
      new TextEncoder().encode(this.sourceJson).length <=
        (this.catalog?.limits.sourceBytes ?? 32768)
    );
  }
  get exactSaved(): boolean {
    return (
      this.canRead() &&
      !this.contractMismatch &&
      !this.accessLost &&
      !!this.saved &&
      this.saved.contractIdentity ===
        this.contractIdentityFor(this.saved.sourceJson) &&
      this.roles.accessProfile()?.actor.actorId === this.actorId &&
      !this.dirty &&
      !!this.scope &&
      this.saved.actorId === this.actorId &&
      this.saved.scopeTypeCode === this.scope.scopeTypeCode &&
      this.saved.scopeKey === this.scope.scopeKey &&
      this.saved.rendererCode === this.catalog?.rendererCode &&
      this.saved.rendererVersion === this.catalog.rendererVersion
    );
  }
  get canRender(): boolean {
    return (
      this.canEdit &&
      this.exactSaved &&
      !!this.catalog?.examples.some((e) => e.code === this.exampleCode)
    );
  }
  get exactProof(): boolean {
    return (
      !!this.proof &&
      this.exactSaved &&
      this.proof.sourceJson === this.saved?.sourceJson &&
      this.proof.sourceHash === this.saved.sourceHash &&
      this.proof.exampleCode === this.exampleCode &&
      this.proof.rendererCode === this.catalog?.rendererCode &&
      this.proof.rendererVersion === this.catalog.rendererVersion &&
      this.proof.isSynthetic
    );
  }
  get canCaptureBaseline(): boolean {
    return this.canEdit && this.exactSaved;
  }
  get canUndoBaseline(): boolean {
    const recovery = this.baselineRecovery;
    return (
      this.canEdit &&
      !!recovery &&
      recovery.expected === this.baseline &&
      recovery.documentIdentity === this.pageViewIdentity &&
      this.matchesSnapshot(recovery.previous)
    );
  }
  get comparison(): NoticeCompositionComparison | null {
    if (!this.baseline || !this.composition) return null;
    return compareNoticeCompositions(
      JSON.parse(this.baseline.sourceJson) as NoticeComposition,
      this.composition,
    );
  }
  get canRenderComparison(): boolean {
    return (
      this.canRender &&
      !!this.baseline &&
      this.matchesSnapshot(this.baseline) &&
      this.comparison?.compatible === true
    );
  }
  get exactComparisonProofs(): {
    baseline: NoticeProof;
    current: NoticeProof;
  } | null {
    const pair = this.comparisonProofs;
    const baseline = this.baseline;
    const saved = this.saved;
    if (
      !pair ||
      !baseline ||
      !saved ||
      !this.exactSaved ||
      !this.matchesSnapshot(baseline) ||
      !this.comparison?.compatible ||
      pair.baseline.sourceJson !== baseline.sourceJson ||
      pair.baseline.sourceHash !== baseline.sourceHash ||
      pair.current.sourceJson !== saved.sourceJson ||
      pair.current.sourceHash !== saved.sourceHash ||
      pair.baseline.contextHash !== pair.current.contextHash ||
      [pair.baseline, pair.current].some(
        (proof) =>
          proof.exampleCode !== this.exampleCode ||
          !proof.isSynthetic ||
          proof.rendererCode !== this.catalog?.rendererCode ||
          proof.rendererVersion !== this.catalog.rendererVersion,
      )
    )
      return null;
    return pair;
  }
  get hasLocalSave(): boolean {
    return !!this.readLocal();
  }
  get localRecovery(): 'available' | 'unverified' | null {
    if (
      !this.scope ||
      !this.canRead() ||
      this.accessLost ||
      this.roles.accessProfile()?.actor.actorId !== this.actorId
    )
      return null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw === null || (this.saved && raw === JSON.stringify(this.saved)))
        return null;
      return this.readLocal() ? 'available' : 'unverified';
    } catch {
      return null;
    }
  }
  get canSaveAndPreview(): boolean {
    return (
      this.canSave &&
      !!this.catalog?.examples.some(
        (example) => example.code === this.exampleCode,
      )
    );
  }
  get usingDocumentContext(): boolean {
    return this.composition?.contractCode === 'notice-document-context-proof';
  }
  get fieldBindings(): { code: string; label: string }[] {
    return (
      (this.usingDocumentContext
        ? this.catalog?.documentContext?.fields
        : this.catalog?.fields) ?? []
    );
  }
  get termBindings(): { code: string; label: string }[] {
    return (
      (this.usingDocumentContext
        ? this.catalog?.documentContext?.terms
        : this.catalog?.terms) ?? []
    );
  }
  knownRunBinding(kind: 'field' | 'term', code: string): boolean {
    return (kind === 'field' ? this.fieldBindings : this.termBindings).some(
      (item) => item.code === code,
    );
  }
  fieldDetails(code: string): NoticeContextField | undefined {
    return this.usingDocumentContext
      ? this.catalog?.documentContext?.fields.find(
          (field) => field.code === code,
        )
      : undefined;
  }
  missingBehaviorLabel(behavior: string): string {
    return behavior === 'withhold_referenced_value'
      ? 'Referenced missing values block output'
      : behavior;
  }
  listDetails(block: NoticeBlock): NoticeContextList | undefined {
    return this.usingDocumentContext
      ? this.catalog?.documentContext?.lists.find(
          (list) => list.code === block.source,
        )
      : undefined;
  }
  columnFields(block: NoticeBlock): { code: string; label: string }[] {
    return this.usingDocumentContext
      ? (this.listDetails(block)?.fields ?? [])
      : [
          { code: 'label', label: 'label' },
          { code: 'value', label: 'value' },
        ];
  }
  columnDetails(
    block: NoticeBlock,
    field: string,
  ): NoticeContextField | undefined {
    return this.listDetails(block)?.fields.find((item) => item.code === field);
  }
  private get contractIdentity(): string {
    return this.contractIdentityFor(this.sourceJson);
  }
  private contractIdentityFor(sourceJson: string): string {
    const catalog = this.catalog;
    if (!catalog) return '';
    const identity = {
      code: catalog.contractCode,
      version: catalog.contractVersion,
      renderer: catalog.rendererCode,
      rendererVersion: catalog.rendererVersion,
      fonts: catalog.fonts,
      assets: catalog.assets,
      limits: catalog.limits,
    };
    try {
      const source: NoticeComposition = JSON.parse(sourceJson);
      if (source.contractCode === 'notice-document-context-proof')
        return JSON.stringify({
          ...identity,
          documentContext: catalog.documentContext ?? null,
        });
    } catch {
      // Raw imports stay exact and untrusted until the server validates them.
    }
    return JSON.stringify(identity);
  }
  private get storageKey(): string {
    return `phms.notice-fidelity.v1:${encodeURIComponent(this.actorId)}:${encodeURIComponent(this.scopeKey)}`;
  }
  closeMoreActions(returnFocus = true): void {
    const details = this.moreActions?.nativeElement;
    if (!details?.open) return;
    details.open = false;
    if (returnFocus)
      this.moreSummary?.nativeElement.focus({ preventScroll: true });
  }
  closeToolsAfterFocus(event: FocusEvent): void {
    if (
      event.relatedTarget instanceof Node &&
      !this.moreActions?.nativeElement.contains(event.relatedTarget)
    )
      this.closeMoreActions(false);
  }
  @HostListener('document:pointerdown', ['$event']) closeToolsOutside(
    event: PointerEvent,
  ): void {
    if (
      event.target instanceof Node &&
      !this.moreActions?.nativeElement.contains(event.target)
    )
      this.closeMoreActions(false);
  }
  loadCatalog(): void {
    if (!this.canRead() || !this.actorId || this.destroyed) return;
    this.cancel();
    this.catalogRequest?.unsubscribe();
    const generation = ++this.catalogGeneration;
    this.loading = true;
    this.catalogRequest = this.api.catalog().subscribe({
      next: (catalog) => {
        if (generation !== this.catalogGeneration || this.destroyed) return;
        this.loading = false;
        if (
          !catalog.isSynthetic ||
          catalog.contractCode !== 'notice-fidelity-experimental' ||
          catalog.contractVersion !== 1
        ) {
          this.history.reset();
          this.resetPageView();
          this.composition = null;
          this.selectedBlockId = null;
          this.view = 'fields';
          this.contractMismatch = true;
          this.error =
            'Unsupported notice proof contract. Reload after the client and renderer are updated together.';
          this.proof = null;
          this.baseline = null;
          return;
        }
        this.contractMismatch = false;
        const previousScope = this.scopeKey;
        const hadAssets =
          !!this.assetCatalog || !!this.composition?.branding.profile;
        if (hadAssets) this.clearAssets();
        this.catalog = catalog;
        if (this.baseline && !this.matchesSnapshot(this.baseline)) {
          this.baseline = null;
          this.baselineDiagnostics = [];
        }
        if (
          this.baselineRecovery &&
          !this.matchesSnapshot(this.baselineRecovery.previous)
        ) {
          this.baselineRecovery = null;
          this.baselineDiagnostics = [];
        }
        this.accessLost = false;
        if (
          previousScope &&
          !catalog.scopes.some((s) => this.scopeValue(s) === previousScope)
        ) {
          this.clearBuffer();
          this.scopeKey = '';
          this.error =
            'Access to the selected scope changed. Protected composition and proof buffers were cleared. Choose an authorized scope.';
          return;
        }
        if (!this.scopeKey && catalog.scopes.length === 1)
          this.scopeKey = this.scopeValue(catalog.scopes[0]);
        if (!this.sourceJson && this.scope && this.untouchedSource === null) {
          this.untouchedSource = catalog.initialSourceJson;
          this.setSource(catalog.initialSourceJson, true);
        }
        if (!catalog.examples.some((e) => e.code === this.exampleCode))
          this.exampleCode = catalog.examples[0]?.code ?? '';
        if (hadAssets) this.syncBrand();
      },
      error: (error) => {
        if (generation !== this.catalogGeneration || this.destroyed) return;
        this.loading = false;
        this.handleError(error);
      },
    });
  }
  async changeScope(value: string): Promise<void> {
    const intent = this.generation;
    if (
      value === this.scopeKey ||
      this.busy ||
      !(await this.allowReplace()) ||
      intent !== this.generation ||
      this.destroyed
    )
      return;
    this.cancel();
    this.history.reset();
    this.resetPageView();
    this.selectedBlockId = null;
    this.baseline = null;
    this.clearAssets();
    this.scopeKey = value;
    this.saved = null;
    this.untouchedSource = null;
    this.sourceJson = '';
    this.composition = null;
    this.diagnostics = [];
    this.error = '';
    this.notice = '';
    if (this.scope && this.catalog) {
      this.untouchedSource = this.catalog.initialSourceJson;
      this.selectedBlockId = null;
      this.setSource(this.catalog.initialSourceJson, true, 'reset');
      this.view = 'page';
    } else this.updateDirty();
  }
  changeExample(value: string): void {
    this.history.breakGroup();
    this.cancel();
    this.exampleCode = value;
    this.notice = '';
  }
  setSource(
    value: string,
    trusted = false,
    historyMode: 'record' | 'reset' | 'skip' = 'record',
    groupKey?: string,
  ): void {
    if (historyMode === 'record' && !this.history.current && this.sourceJson)
      this.history.reset({
        sourceJson: this.sourceJson,
        selectedBlockId: this.selectedBlockId,
        structured: !!this.composition,
      });
    this.cancel();
    this.sourceJson = value;
    if (historyMode === 'reset') this.resetPageView();
    this.pageFocusBlockOnOpen = null;
    this.pageIssueFocus = null;
    if (
      trusted &&
      this.baseline &&
      this.baseline.contractIdentity !== this.contractIdentityFor(value)
    )
      this.baseline = null;
    if (historyMode === 'reset')
      this.history.reset({
        sourceJson: value,
        selectedBlockId: this.selectedBlockId,
        structured: trusted,
      });
    else if (historyMode === 'record')
      this.history.record(
        {
          sourceJson: value,
          selectedBlockId: this.selectedBlockId,
          structured: trusted,
        },
        groupKey,
      );
    this.diagnostics = [];
    this.error = '';
    this.notice = '';
    try {
      if (!trusted) {
        this.composition = null;
        this.syncBrand();
        this.updateDirty();
        return;
      }
      const parsed: NoticeComposition = JSON.parse(value);
      // Only server-canonical or native structured edits enter the form model. Raw imports
      // remain exact text until validation, including duplicate or unsupported properties.
      this.composition =
        parsed &&
        parsed.page &&
        parsed.branding &&
        Array.isArray(parsed.blocks) &&
        parsed.blocks.every(
          (b) =>
            b &&
            typeof b.id === 'string' &&
            ['paragraph', 'box', 'repeat', 'pageBreak'].includes(b.kind) &&
            (b.kind === 'pageBreak' || !!b.style) &&
            (['paragraph', 'box'].includes(b.kind)
              ? Array.isArray(b.runs)
              : b.kind !== 'repeat' || Array.isArray(b.columns)),
        )
          ? parsed
          : null;
    } catch {
      this.composition = null;
    }
    this.syncBrand();
    this.updateDirty();
  }
  edited(): void {
    if (this.composition)
      this.setSource(JSON.stringify(this.composition, null, 2), true);
  }
  pageChanged(change: NoticePageChange): void {
    if (!this.canEdit) return;
    this.selectedBlockId = change.selectedBlockId;
    this.setSource(
      JSON.stringify(change.composition, null, 2),
      true,
      'record',
      change.groupKey,
    );
    change.accepted = true;
  }
  selectPageBlock(id: string): void {
    if (!this.canEdit || id === this.selectedBlockId) return;
    this.history.breakGroup();
    this.selectedBlockId = id;
    this.history.replaceCurrent({
      sourceJson: this.sourceJson,
      selectedBlockId: id,
      structured: !!this.composition,
    });
  }
  rememberPageView(change: NoticePageViewStateChange): void {
    if (
      change.identity === this.pageViewIdentity &&
      this.canRead() &&
      !this.accessLost &&
      this.roles.accessProfile()?.actor.actorId === this.actorId
    )
      this.pageViewState = change.state;
  }
  private resetPageView(): void {
    this.pageViewIdentity++;
    this.pageViewState = null;
    this.baselineRecovery = null;
    this.baselineDiagnostics = [];
    this.pageIssueFocus = null;
  }
  navigateIssue(request: NoticeDiagnosticNavigation): void {
    if (!this.canEdit || !this.diagnostics.includes(request.diagnostic)) return;
    const target = resolveDiagnosticTarget(
      request.diagnostic,
      this.composition,
      this.catalog,
    ).targets.find(
      (candidate) =>
        JSON.stringify(candidate) === JSON.stringify(request.target),
    );
    if (!target) return;
    if (target.kind === 'fields') {
      this.inspectIssueSource();
      return;
    }
    const blockId =
      target.kind === 'reference'
        ? target.selection.blockId
        : 'blockId' in target
          ? target.blockId
          : null;
    if (blockId) this.selectPageBlock(blockId);
    this.pageFocusBlockOnOpen = null;
    this.pageIssueFocus = target;
    this.view = 'page';
  }
  issueFocusUnavailable(): void {
    if (this.pageIssueFocus && this.view === 'page') this.inspectIssueSource();
  }
  private inspectIssueSource(): void {
    if (!this.canEdit) return;
    this.pageIssueFocus = null;
    this.pageFocusBlockOnOpen = null;
    this.view = 'fields';
    const generation = this.generation;
    const source = this.sourceJson;
    const scopeKey = this.scopeKey;
    const active = this.sourceInput?.nativeElement.ownerDocument.activeElement;
    requestAnimationFrame(() => {
      if (
        !this.current(generation, source, scopeKey) ||
        !this.canEdit ||
        this.view !== 'fields' ||
        this.sourceInput?.nativeElement.ownerDocument.activeElement !== active
      )
        return;
      if (this.sourceDetails) this.sourceDetails.nativeElement.open = true;
      this.sourceInput?.nativeElement.focus();
      this.sourceInput?.nativeElement.scrollIntoView?.({ block: 'nearest' });
    });
  }
  historyCommand(command: 'undo' | 'redo'): void {
    if (!this.canEdit) return;
    const next = command === 'undo' ? this.history.undo() : this.history.redo();
    if (!next) return;
    this.selectedBlockId = next.selectedBlockId;
    this.setSource(next.sourceJson, next.structured === true, 'skip');
  }
  pageAccessLost(): void {
    this.clearProtected();
    this.error =
      'Notice proof access changed. Protected buffers were cleared. Refresh access before continuing.';
  }
  changeBodySize(value: number): void {
    this.documentSetting({ section: 'page', property: 'fontSizePt', value });
  }
  documentSetting(setting: NoticeDocumentSetting): void {
    if (!this.canEdit || !this.composition || !this.catalog) return;
    const result = applyNoticeDocumentSetting(
      this.composition,
      this.catalog,
      setting,
      this.assetCatalog,
    );
    if (!result.ok) {
      this.error = `This change was not applied. ${result.message}`;
      return;
    }
    if (JSON.stringify(result.composition) === JSON.stringify(this.composition))
      return;
    this.setSource(
      JSON.stringify(result.composition, null, 2),
      true,
      'record',
      setting.section === 'branding' &&
        (setting.property === 'headerText' || setting.property === 'footerText')
        ? `document:${setting.property}`
        : undefined,
    );
  }
  private changeStructure(command: NoticeStructureCommand): void {
    if (!this.canEdit || !this.composition || !this.catalog) return;
    const result = applyNoticeStructureCommand(
      this.composition,
      this.catalog,
      command,
    );
    if (!result.ok) {
      this.error = result.message;
      return;
    }
    this.selectedBlockId = result.selectedBlockId;
    this.setSource(JSON.stringify(result.composition, null, 2), true);
  }
  addBlock(kind: NoticeBlock['kind']): void {
    const anchor = this.composition?.blocks.at(-1);
    if (anchor)
      this.changeStructure({
        type: 'insert',
        kind,
        anchorId: anchor.id,
        position: kind === 'pageBreak' ? 'before' : 'after',
      });
  }
  move(index: number, direction: number): void {
    const block = this.composition?.blocks[index];
    if (block && (direction === -1 || direction === 1))
      this.changeStructure({ type: 'move', blockId: block.id, direction });
  }
  removeBlock(index: number): void {
    const block = this.composition?.blocks[index];
    if (block) this.changeStructure({ type: 'remove', blockId: block.id });
  }
  addRun(block: NoticeBlock): void {
    if (this.canEdit && block.runs && block.runs.length < 64) {
      block.runs.push({
        kind: 'literal',
        value: '',
        bold: false,
        italic: false,
      });
      this.edited();
    }
  }
  removeRun(block: NoticeBlock, index: number): void {
    if (this.canEdit && block.runs && block.runs.length > 1) {
      block.runs.splice(index, 1);
      this.edited();
    }
  }
  addColumn(block: NoticeBlock): void {
    this.changeStructure({ type: 'column-add', blockId: block.id });
  }
  removeColumn(block: NoticeBlock, index: number): void {
    const column = block.columns?.[index];
    if (column)
      this.changeStructure({
        type: 'column-remove',
        blockId: block.id,
        columnId: column.id,
      });
  }
  runKind(
    block: NoticeBlock,
    index: number,
    kind: 'literal' | 'field' | 'term',
  ): void {
    const run = block.runs?.[index];
    if (!run || !this.canEdit) return;
    run.kind = kind;
    run.value =
      kind === 'literal'
        ? ''
        : ((kind === 'field'
            ? this.fieldBindings[0]?.code
            : this.termBindings[0]?.code) ?? '');
    delete run.format;
    if (kind === 'field' && this.usingDocumentContext)
      run.format = this.fieldDetails(run.value)?.formats[0];
    this.edited();
  }
  private referenceAtRun(
    block: NoticeBlock,
    index: number,
  ): NoticeReferenceSelection | undefined {
    const run = block.runs?.[index];
    if (!run || run.kind === 'literal') return undefined;
    const referenceIndex = block
      .runs!.slice(0, index)
      .filter((item) => item.kind !== 'literal').length;
    return noticeReferenceSelections(block)[referenceIndex];
  }
  private changeReference(
    selection: NoticeReferenceSelection,
    command: NoticeReferenceCommand,
  ): void {
    if (!this.canEdit || !this.composition || !this.catalog) return;
    const result = applyNoticeReferenceCommand(
      this.composition,
      this.catalog,
      selection,
      command,
    );
    if (!result.ok) {
      this.error = `This change was not applied. ${result.message}`;
      return;
    }
    if (JSON.stringify(result.composition) === JSON.stringify(this.composition))
      return;
    this.setSource(JSON.stringify(result.composition, null, 2), true);
  }
  runValue(block: NoticeBlock, index: number, value: string): void {
    const run = block.runs?.[index];
    if (!run || !this.canEdit) return;
    if (run.kind !== 'literal') {
      const selection = this.referenceAtRun(block, index);
      if (selection)
        this.changeReference(selection, { type: 'binding', code: value });
      return;
    }
    run.value = value;
    delete run.format;
    this.edited();
  }
  runFormat(block: NoticeBlock, index: number, format: string | null): void {
    const selection = this.referenceAtRun(block, index);
    if (selection) this.changeReference(selection, { type: 'format', format });
  }
  listSource(block: NoticeBlock, value: string): void {
    this.changeStructure({ type: 'list', blockId: block.id, source: value });
  }
  columnField(block: NoticeBlock, index: number, value: string): void {
    const column = block.columns?.[index];
    if (column)
      this.changeStructure({
        type: 'column-edit',
        blockId: block.id,
        columnId: column.id,
        property: 'field',
        value,
      });
  }
  async useDocumentContext(): Promise<void> {
    const intent = this.generation;
    if (
      !this.canEdit ||
      !this.catalog?.documentContext ||
      !this.catalog.documentSourceJson
    )
      return;
    const allowed = this.dirty
      ? await this.allowReplace()
      : !(this.saved || this.hasLocalSave) ||
        (await this.confirmation.confirm({
          heading: 'Use registered document context?',
          message:
            'Open a separate synthetic composition. Your existing local save remains available until you choose Save locally.',
          confirmText: 'Use context',
          cancelText: 'Keep composition',
        }));
    if (
      !allowed ||
      intent !== this.generation ||
      !this.canEdit ||
      !this.catalog?.documentSourceJson
    )
      return;
    this.saved = null;
    this.untouchedSource = null;
    this.selectedBlockId = null;
    this.setSource(this.catalog.documentSourceJson, true, 'reset');
    this.view = 'page';
  }
  saveAndPreview(): void {
    if (this.exactSaved) this.render();
    else if (this.canSaveAndPreview) this.save(true);
  }
  save(preview = false): void {
    const scope = this.scope;
    if (!scope || !this.canSave) return;
    let stored: string | null;
    try {
      stored = localStorage.getItem(this.storageKey);
    } catch {
      this.error =
        'Browser storage is unavailable. Your edits remain open; allow local storage and save again.';
      return;
    }
    const intent = this.generation;
    const source = this.sourceJson;
    const scopeKey = this.scopeKey;
    if (
      stored !== null &&
      (!this.saved || stored !== JSON.stringify(this.saved))
    ) {
      this.confirmingSave = true;
      void this.confirmation
        .confirm({
          heading: 'Replace the local save?',
          message:
            'A different local save exists for this actor and scope. Replace it with the composition open here? You can resume the saved work instead.',
          confirmText: 'Replace local save',
          cancelText: 'Keep local save',
        })
        .then((allowed) => {
          this.confirmingSave = false;
          if (
            !allowed ||
            !this.current(intent, source, scopeKey) ||
            !this.canSave
          )
            return;
          this.validateAndSave(stored, preview);
        });
      return;
    }
    this.validateAndSave(stored, preview);
  }
  private validateAndSave(stored: string | null, preview: boolean): void {
    const scope = this.scope;
    if (!scope || !this.canSave || !this.storageUnchanged(stored)) return;
    this.history.breakGroup();
    this.cancel();
    const generation = this.generation;
    const source = this.sourceJson;
    const scopeKey = this.scopeKey;
    this.state.busy.set(true);
    this.error = '';
    this.diagnostics = [];
    // Keep cancellation attached to validation even when a synchronous response
    // immediately starts the next render request.
    const request = new Subscription();
    this.request = request;
    request.add(
      this.api.validate(scope, source).subscribe({
        next: (result) => {
          if (!this.current(generation, source, scopeKey)) return;
          this.state.busy.set(false);
          if (
            !result.isValid ||
            !result.sourceJson ||
            !result.sourceHash ||
            !/^[a-f0-9]{64}$/.test(result.sourceHash)
          ) {
            this.diagnostics = result.diagnostics;
            this.error =
              'The composition was not saved. Resolve the validation diagnostics.';
            return;
          }
          const saved: SavedNoticeSource = {
            contractIdentity: this.contractIdentity,
            actorId: this.actorId,
            scopeTypeCode: scope.scopeTypeCode,
            scopeKey: scope.scopeKey,
            sourceJson: result.sourceJson,
            sourceHash: result.sourceHash,
            rendererCode: this.catalog!.rendererCode,
            rendererVersion: this.catalog!.rendererVersion,
          };
          if (!this.storageUnchanged(stored)) return;
          try {
            localStorage.setItem(this.storageKey, JSON.stringify(saved));
          } catch {
            this.error =
              'Browser storage is unavailable. Your edits remain open; allow local storage and save again.';
            return;
          }
          this.saved = saved;
          this.setSource(saved.sourceJson, true, 'skip');
          this.history.replaceCurrent({
            sourceJson: saved.sourceJson,
            selectedBlockId: this.selectedBlockId,
            structured: true,
          });
          this.notice =
            'Saved locally in this browser for this actor and scope.';
          if (preview) this.render();
        },
        error: (error) => {
          if (!this.current(generation, source, scopeKey)) return;
          this.state.busy.set(false);
          this.handleError(error);
        },
      }),
    );
  }
  private storageUnchanged(stored: string | null): boolean {
    try {
      if (localStorage.getItem(this.storageKey) === stored) return true;
      this.error =
        'The local save changed in another tab. Resume the saved work or save again to choose whether to replace it.';
    } catch {
      this.error =
        'Browser storage is unavailable. Your edits remain open; allow local storage and save again.';
    }
    return false;
  }
  render(): void {
    const scope = this.scope;
    const saved = this.saved;
    if (!scope || !saved || !this.canRender) return;
    this.history.breakGroup();
    this.view = 'preview';
    this.cancel();
    const generation = this.generation;
    const source = this.sourceJson;
    const scopeKey = this.scopeKey;
    const example = this.exampleCode;
    const terminologyRevision = this.terminologyRefresh.counter();
    this.state.busy.set(true);
    this.error = '';
    this.diagnostics = [];
    this.request = this.api
      .render(scope, saved.sourceJson, saved.sourceHash, example)
      .subscribe({
        next: (proof) => {
          if (
            !this.current(generation, source, scopeKey) ||
            example !== this.exampleCode
          )
            return;
          this.state.busy.set(false);
          if (
            proof.terminologySnapshot &&
            terminologyRevision !== this.terminologyRefresh.counter()
          ) {
            this.invalidateTerminologyProof(
              this.terminologyRefresh.status() === 'unavailable',
            );
            return;
          }
          if (!this.acceptsProof(proof, saved, scope, example)) {
            this.error =
              'The renderer returned a proof for a different source or an incomplete output. Save and render again.';
            return;
          }
          this.proof = proof;
          this.diagnostics = proof.diagnostics;
          this.notice = 'Synthetic PDF ready for the exact local save.';
        },
        error: (error) => {
          if (!this.current(generation, source, scopeKey)) return;
          this.state.busy.set(false);
          this.handleError(error);
        },
      });
  }
  captureBaseline(): void {
    const scope = this.scope;
    const saved = this.saved;
    if (!scope || !saved || !this.canCaptureBaseline) return;
    const previous = this.baseline;
    this.baselineDiagnostics = [];
    this.history.breakGroup();
    this.cancel();
    const generation = this.generation;
    const source = this.sourceJson;
    const scopeKey = this.scopeKey;
    this.state.busy.set(true);
    this.error = '';
    this.notice = '';
    this.request = this.api.validate(scope, saved.sourceJson).subscribe({
      next: (result) => {
        if (
          !this.current(generation, source, scopeKey) ||
          this.baseline !== previous
        )
          return;
        this.state.busy.set(false);
        if (
          !result.isValid ||
          result.sourceJson !== saved.sourceJson ||
          result.sourceHash !== saved.sourceHash ||
          !this.matchesSnapshot(saved)
        ) {
          this.diagnostics = result.diagnostics;
          this.error =
            'The saved source could not be verified for comparison. Save locally and capture again.';
          return;
        }
        const next = { ...saved };
        if (
          previous &&
          (previous.sourceJson !== next.sourceJson ||
            previous.sourceHash !== next.sourceHash)
        ) {
          this.baselineRecovery = {
            previous,
            expected: next,
            documentIdentity: this.pageViewIdentity,
          };
        }
        this.baseline =
          previous?.sourceJson === next.sourceJson &&
          previous.sourceHash === next.sourceHash
            ? previous
            : next;
        this.diagnostics = [];
        this.notice =
          this.baseline === previous
            ? 'Baseline already matches the saved source.'
            : previous
              ? 'Baseline replaced. Undo baseline change restores the previous baseline.'
              : 'Captured a verified session baseline.';
      },
      error: (error) => {
        if (!this.current(generation, source, scopeKey)) return;
        this.state.busy.set(false);
        this.handleError(error);
      },
    });
  }
  clearBaseline(): void {
    if (!this.canEdit || !this.baseline) return;
    this.baselineDiagnostics = [];
    this.baselineRecovery = {
      previous: this.baseline,
      expected: null,
      documentIdentity: this.pageViewIdentity,
    };
    this.cancel();
    this.baseline = null;
    this.notice = 'Baseline cleared. Undo baseline change restores it.';
  }
  undoBaselineChange(): void {
    const recovery = this.baselineRecovery;
    const scope = this.scope;
    if (!recovery || !scope || !this.canUndoBaseline) return;
    this.history.breakGroup();
    this.cancel();
    const generation = this.generation;
    const source = this.sourceJson;
    const scopeKey = this.scopeKey;
    this.state.busy.set(true);
    this.error = '';
    this.notice = '';
    this.baselineDiagnostics = [];
    this.request = this.api
      .validate(scope, recovery.previous.sourceJson)
      .subscribe({
        next: (result) => {
          if (
            !this.current(generation, source, scopeKey) ||
            this.baselineRecovery !== recovery ||
            this.baseline !== recovery.expected
          )
            return;
          this.state.busy.set(false);
          if (!this.canUndoBaseline) return;
          if (
            !result.isValid ||
            result.sourceJson !== recovery.previous.sourceJson ||
            result.sourceHash !== recovery.previous.sourceHash
          ) {
            this.error =
              'The previous baseline could not be verified. Your source and current baseline are unchanged.';
            this.baselineDiagnostics = result.diagnostics;
            return;
          }
          this.baseline = recovery.previous;
          this.baselineRecovery = null;
          this.notice =
            'Previous baseline restored. Render comparison PDFs to review it.';
        },
        error: (error) => {
          if (!this.current(generation, source, scopeKey)) return;
          this.state.busy.set(false);
          this.handleBaselineFailure(
            error,
            'The previous baseline could not be verified. Your source and current baseline are unchanged; retry when verification is available.',
          );
        },
      });
  }
  private handleBaselineFailure(error: unknown, message: string): void {
    if (
      error instanceof HttpErrorResponse &&
      [401, 403].includes(error.status)
    ) {
      this.handleError(error);
      return;
    }
    if (
      error instanceof HttpErrorResponse &&
      Array.isArray(error.error?.diagnostics)
    )
      this.baselineDiagnostics = error.error.diagnostics;
    this.error = message;
  }
  editComparedBlock(id: string): void {
    if (
      !this.canEdit ||
      !this.composition?.blocks.some((block) => block.id === id)
    )
      return;
    this.selectPageBlock(id);
    this.pageFocusBlockOnOpen = id;
    this.view = 'page';
  }
  renderComparison(): void {
    const scope = this.scope;
    const saved = this.saved;
    const baseline = this.baseline;
    if (!scope || !saved || !baseline || !this.canRenderComparison) return;
    this.history.breakGroup();
    this.cancel();
    this.view = 'review';
    const generation = this.generation;
    const source = this.sourceJson;
    const scopeKey = this.scopeKey;
    const example = this.exampleCode;
    const terminologyRevision = this.terminologyRefresh.counter();
    this.state.busy.set(true);
    this.error = '';
    this.notice = '';
    this.diagnostics = [];
    this.baselineDiagnostics = [];
    this.request = forkJoin({
      baseline: this.api
        .render(scope, baseline.sourceJson, baseline.sourceHash, example)
        .pipe(
          catchError((error: unknown) =>
            throwError(() => new NoticeComparisonFailure('baseline', error)),
          ),
        ),
      current: this.api
        .render(scope, saved.sourceJson, saved.sourceHash, example)
        .pipe(
          catchError((error: unknown) =>
            throwError(() => new NoticeComparisonFailure('current', error)),
          ),
        ),
    }).subscribe({
      next: (pair) => {
        if (
          !this.current(generation, source, scopeKey) ||
          baseline !== this.baseline ||
          example !== this.exampleCode
        )
          return;
        this.state.busy.set(false);
        if (
          (pair.current.terminologySnapshot ||
            pair.baseline.terminologySnapshot) &&
          terminologyRevision !== this.terminologyRefresh.counter()
        ) {
          this.invalidateTerminologyProof(
            this.terminologyRefresh.status() === 'unavailable',
          );
          return;
        }
        if (
          !this.acceptsProof(pair.baseline, baseline, scope, example) ||
          !this.acceptsProof(pair.current, saved, scope, example)
        ) {
          this.error =
            'The renderer returned a different source or incomplete comparison. Both proofs were withheld; save and render again.';
          return;
        }
        if (pair.baseline.contextHash !== pair.current.contextHash) {
          this.error =
            'The context changed between the two renders. Both proofs were withheld; render the comparison again.';
          return;
        }
        this.comparisonProofs = pair;
        this.proof = pair.current;
        this.diagnostics = pair.current.diagnostics;
        this.notice =
          'Both synthetic proofs use the exact saved sources and the same captured context. This session comparison does not record production review or publication.';
      },
      error: (error) => {
        if (!this.current(generation, source, scopeKey)) return;
        this.state.busy.set(false);
        if (
          error instanceof NoticeComparisonFailure &&
          error.side === 'baseline'
        )
          this.handleBaselineFailure(
            error.error,
            'The captured baseline PDF could not be rendered. Your current source is unchanged; inspect the baseline issues or retry.',
          );
        else
          this.handleError(
            error instanceof NoticeComparisonFailure ? error.error : error,
          );
      },
    });
  }
  exportComparisonPdf(side: 'baseline' | 'current'): void {
    const pair = this.exactComparisonProofs;
    if (!pair || this.busy) return;
    this.download(
      Uint8Array.from(atob(pair[side].pdfBase64), (c) => c.charCodeAt(0)),
      'application/pdf',
      `synthetic-notice-${side}-${this.exampleCode}.pdf`,
    );
  }
  private matchesSnapshot(saved: SavedNoticeSource): boolean {
    const scope = this.scope;
    return (
      this.canRead() &&
      !this.accessLost &&
      !this.contractMismatch &&
      !!scope &&
      this.roles.accessProfile()?.actor.actorId === this.actorId &&
      saved.actorId === this.actorId &&
      saved.scopeTypeCode === scope.scopeTypeCode &&
      saved.scopeKey === scope.scopeKey &&
      saved.contractIdentity === this.contractIdentityFor(saved.sourceJson) &&
      saved.rendererCode === this.catalog?.rendererCode &&
      saved.rendererVersion === this.catalog.rendererVersion &&
      /^[a-f0-9]{64}$/.test(saved.sourceHash)
    );
  }
  private acceptsProof(
    proof: NoticeProof,
    saved: SavedNoticeSource,
    scope: NoticeScope,
    example: string,
  ): boolean {
    return (
      !!proof &&
      this.matchesSnapshot(saved) &&
      proof.sourceJson === saved.sourceJson &&
      proof.sourceHash === saved.sourceHash &&
      proof.exampleCode === example &&
      proof.isSynthetic === true &&
      /^[a-f0-9]{64}$/.test(proof.contextHash) &&
      Array.isArray(proof.diagnostics) &&
      this.matchContextEvidence(proof, saved.sourceJson, scope) &&
      matchNoticeReferenceEvidence(proof) &&
      !!this.catalog &&
      matchNoticeTerminologyEvidence(proof, this.catalog, scope) &&
      proof.rendererCode === this.catalog?.rendererCode &&
      proof.rendererVersion === this.catalog.rendererVersion &&
      !!proof.pdfBase64 &&
      /^[a-f0-9]{64}$/.test(proof.pdfHash) &&
      Number.isInteger(proof.pageCount) &&
      proof.pageCount >= 1 &&
      proof.pageCount <= this.catalog.limits.pages &&
      Array.isArray(proof.pages) &&
      proof.pages.length === proof.pageCount &&
      this.matchEvidence(proof, saved.sourceJson) &&
      proof.pages.every(
        (p, i) =>
          !!p &&
          p.number === i + 1 &&
          !!p.pngBase64 &&
          Number.isFinite(p.widthPx) &&
          p.widthPx > 0 &&
          Number.isFinite(p.heightPx) &&
          p.heightPx > 0 &&
          /^[a-f0-9]{64}$/.test(p.sha256),
      )
    );
  }
  cancel(): void {
    this.generation++;
    this.request?.unsubscribe();
    this.request = undefined;
    this.state.busy.set(false);
    this.proof = null;
    this.comparisonProofs = null;
  }
  @HostListener('window:focus') checkWording(): void {
    const saved = this.saved;
    const scope = this.scope;
    const proof = this.proof;
    if (
      !saved ||
      !scope ||
      !proof?.terminologySnapshot ||
      !this.exactProof ||
      this.busy ||
      !this.canEdit
    )
      return;
    const generation = this.generation,
      source = this.sourceJson,
      scopeKey = this.scopeKey;
    const terminologyRevision = this.terminologyRefresh.counter();
    const pair = this.exactComparisonProofs;
    const captures = pair
      ? [pair.baseline, pair.current].filter((p) => !!p.terminologySnapshot)
      : [proof];
    this.state.busy.set(true);
    this.error = '';
    this.request = forkJoin(
      captures.map((p) =>
        this.api.terminologyFreshness(
          scope,
          p.sourceJson,
          p.sourceHash,
          p.terminologySnapshot!.semanticHash,
        ),
      ),
    ).subscribe({
      next: (results) => {
        if (!this.current(generation, source, scopeKey) || this.proof !== proof)
          return;
        this.state.busy.set(false);
        if (
          terminologyRevision !== this.terminologyRefresh.counter() ||
          results.length !== captures.length ||
          results.some(
            (result, i) =>
              !result ||
              result.isCurrent !== true ||
              result.sourceHash !== captures[i].sourceHash ||
              result.semanticHash !==
                captures[i].terminologySnapshot!.semanticHash,
          )
        ) {
          this.invalidateTerminologyProof(false);
          return;
        }
        this.notice =
          'Published wording checked. The captured proof is current.';
      },
      error: (error: unknown) => {
        if (!this.current(generation, source, scopeKey) || this.proof !== proof)
          return;
        if (
          error instanceof HttpErrorResponse &&
          [401, 403].includes(error.status)
        ) {
          this.handleError(error);
          return;
        }
        this.invalidateTerminologyProof(true);
      },
    });
  }
  private invalidateTerminologyProof(unavailable: boolean): void {
    this.cancel();
    this.notice = '';
    this.error = unavailable
      ? 'Published wording is unavailable. Render again when terminology can be checked.'
      : 'Captured wording is stale. Render a new proof using current published wording.';
  }
  async reloadLocal(): Promise<void> {
    const intent = this.generation;
    if (
      !this.canEdit ||
      !(await this.allowReplace()) ||
      intent !== this.generation ||
      !this.canEdit
    )
      return;
    const local = this.readLocal();
    if (!local) {
      this.error =
        'No compatible local save is available for this actor and scope.';
      return;
    }
    const scope = this.scope!;
    this.saved = null;
    this.selectedBlockId = null;
    this.setSource(local.sourceJson, false, 'reset');
    this.view = 'fields';
    const generation = this.generation;
    const scopeKey = this.scopeKey;
    this.state.busy.set(true);
    this.request = this.api.validate(scope, local.sourceJson).subscribe({
      next: (result) => {
        if (!this.current(generation, local.sourceJson, scopeKey)) return;
        this.state.busy.set(false);
        if (
          !result.isValid ||
          result.sourceJson !== local.sourceJson ||
          result.sourceHash !== local.sourceHash
        ) {
          this.diagnostics = result.diagnostics;
          this.error =
            'The local save could not be verified. Its exact source remains open in Fields; resolve diagnostics before saving.';
          return;
        }
        this.saved = local;
        this.setSource(local.sourceJson, true, 'reset');
        this.view = 'page';
        this.notice = 'Verified and reloaded the local save.';
      },
      error: (error) => {
        if (!this.current(generation, local.sourceJson, scopeKey)) return;
        this.state.busy.set(false);
        this.handleError(error);
      },
    });
  }

  async resetExample(): Promise<void> {
    const intent = this.generation;
    if (
      this.canEdit &&
      this.catalog &&
      (await this.allowReplace()) &&
      intent === this.generation &&
      this.canEdit
    ) {
      this.saved = null;
      this.untouchedSource = null;
      this.selectedBlockId = null;
      this.setSource(this.catalog.initialSourceJson, true, 'reset');
      this.view = 'page';
    }
  }
  async importFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const intent = this.generation;
    if (
      !file ||
      !this.canEdit ||
      !(await this.allowReplace()) ||
      intent !== this.generation ||
      !this.canEdit
    )
      return;
    if (file.size > (this.catalog?.limits.sourceBytes ?? 32768)) {
      this.error =
        'The JSON file exceeds the 32 KiB composition limit. Reduce the source and try again.';
      return;
    }
    const generation = this.generation;
    const scopeKey = this.scopeKey;
    try {
      const text = await file.text();
      if (
        !this.destroyed &&
        generation === this.generation &&
        scopeKey === this.scopeKey &&
        this.canEdit
      ) {
        this.saved = null;
        this.selectedBlockId = null;
        this.setSource(text, false, 'reset');
        this.view = 'fields';
      }
    } catch {
      this.error =
        'The file could not be read. Your existing composition is unchanged.';
    }
  }
  exportSource(): void {
    if (this.exactSaved && !this.busy)
      this.download(
        this.saved!.sourceJson,
        'application/json',
        'notice-composition.json',
      );
  }
  exportPdf(): void {
    if (this.exactProof && !this.busy)
      this.download(
        Uint8Array.from(atob(this.proof!.pdfBase64), (c) => c.charCodeAt(0)),
        'application/pdf',
        `synthetic-notice-${this.exampleCode}.pdf`,
      );
  }
  private download(
    content: string | Uint8Array,
    type: string,
    name: string,
  ): void {
    const blob = new Blob([content as BlobPart], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  private matchEvidence(proof: NoticeProof, sourceJson: string): boolean {
    if (
      !this.catalog ||
      !Array.isArray(proof.fontEvidence) ||
      !Array.isArray(proof.assetEvidence)
    )
      return false;
    try {
      const source: NoticeComposition = JSON.parse(sourceJson);
      if (source.branding.profile) {
        const brand = this.assetCatalog?.brands.find((item) =>
          sameBrandReference(item.reference, source.branding.profile),
        );
        const evidence = proof.brandEvidence;
        if (
          !brand ||
          !this.assetCatalog?.fonts.some(
            (font) =>
              font.available &&
              font.code === brand.style.fontCode &&
              font.sha256 === brand.style.fontSha256,
          ) ||
          !this.catalog.fonts.some(
            (font) =>
              font.code === brand.style.fontCode &&
              font.sha256 === brand.style.fontSha256,
          ) ||
          (brand.style.logo &&
            !this.assetCatalog.items.some((item) =>
              sameAssetReference(item.reference, brand.style.logo),
            )) ||
          !evidence ||
          !sameBrandReference(evidence.reference, brand.reference) ||
          evidence.name !== brand.name ||
          evidence.scopeTypeCode !== brand.scopeTypeCode ||
          evidence.scopeKey !== brand.scopeKey ||
          JSON.stringify(evidence.style) !== JSON.stringify(brand.style)
        )
          return false;
        const expected = [
          {
            code: `brand:${brand.reference.brandId}:v${brand.reference.version}`,
            sha256: brand.reference.sha256,
          },
          ...(brand.style.logo
            ? [
                {
                  code: `asset:${brand.style.logo.assetId}:v${brand.style.logo.version}`,
                  sha256: brand.style.logo.sha256,
                },
              ]
            : []),
        ];
        return (
          proof.fontEvidence.length === 1 &&
          proof.fontEvidence[0].code === brand.style.fontCode &&
          proof.fontEvidence[0].sha256 === brand.style.fontSha256 &&
          proof.assetEvidence.length === expected.length &&
          new Set(proof.assetEvidence.map((item) => item.code)).size ===
            expected.length &&
          expected.every((item) =>
            proof.assetEvidence.some(
              (actual) =>
                actual.code === item.code && actual.sha256 === item.sha256,
            ),
          )
        );
      }
      if (proof.brandEvidence) return false;
      const fonts = this.catalog.fonts.filter(
        (font) => font.code === source.page.fontCode,
      );
      const codes = [
        source.branding.logoCode,
        source.branding.watermarkCode,
      ].filter((code) => code !== 'none');
      const assets = this.catalog.assets.filter((asset) =>
        codes.includes(asset.code),
      );
      const equal = (
        actual: { code: string; sha256: string }[],
        expected: { code: string; sha256: string }[],
      ) =>
        actual.length === expected.length &&
        new Set(actual.map((item) => item.code)).size === actual.length &&
        expected.every((item) =>
          actual.some(
            (value) => value.code === item.code && value.sha256 === item.sha256,
          ),
        );
      return (
        fonts.length === 1 &&
        assets.length === codes.length &&
        equal(proof.fontEvidence, fonts) &&
        equal(proof.assetEvidence, assets)
      );
    } catch {
      return false;
    }
  }
  private matchContextEvidence(
    proof: NoticeProof,
    sourceJson: string,
    scope: NoticeScope,
  ): boolean {
    try {
      const source: NoticeComposition = JSON.parse(sourceJson);
      if (source.contractCode !== 'notice-document-context-proof')
        return proof.diagnostics.length === 0 && !proof.contextEvidence;
      const evidence = proof.contextEvidence;
      const context = source.context;
      const profile = this.catalog?.documentContext;
      if (!evidence || !context || !profile) return false;
      if (
        !evidence.terms ||
        typeof evidence.terms !== 'object' ||
        Array.isArray(evidence.terms) ||
        !Array.isArray(evidence.termEvidence) ||
        profile.terms.length > 30 ||
        evidence.termEvidence.length !== profile.terms.length ||
        Object.keys(evidence.terms).length !== profile.terms.length
      )
        return false;
      const termCodes = new Set<string>();
      const termsMatch = evidence.termEvidence.every((term) => {
        if (
          !term ||
          typeof term !== 'object' ||
          Array.isArray(term) ||
          typeof term.code !== 'string' ||
          termCodes.has(term.code)
        )
          return false;
        termCodes.add(term.code);
        const declared = profile.terms.find((item) => item.code === term.code);
        return (
          !!declared &&
          term.tokenCode === declared.tokenCode &&
          term.configurationCode === declared.configurationCode &&
          typeof term.text === 'string' &&
          term.text.trim().length > 0 &&
          term.text.length <= 2000 &&
          typeof term.usedFallback === 'boolean' &&
          typeof term.reasonCode === 'string' &&
          term.reasonCode.trim().length > 0 &&
          term.reasonCode.length <= 512 &&
          (term.configurationVersionCode == null ||
            (typeof term.configurationVersionCode === 'string' &&
              term.configurationVersionCode.length <= 512)) &&
          evidence.terms[term.code] === term.text
        );
      });
      const diagnostic = (item: NoticeDiagnostic) =>
        !!item &&
        item.code === 'document_context.terminology_fallback' &&
        typeof item.path === 'string' &&
        item.path.startsWith('$.terms.') &&
        item.path.length <= 512 &&
        typeof item.message === 'string' &&
        item.message.length > 0 &&
        item.message.length <= 2000 &&
        typeof item.action === 'string' &&
        item.action.length > 0 &&
        item.action.length <= 2000;
      return (
        context.code === profile.code &&
        context.version === profile.version &&
        context.hash === profile.hash &&
        evidence.reference?.code === context.code &&
        evidence.reference.version === context.version &&
        evidence.reference.hash === context.hash &&
        evidence.sourceKind === 'synthetic_example' &&
        evidence.sourceKey === proof.exampleCode &&
        evidence.isSynthetic === true &&
        evidence.scopeTypeCode === scope.scopeTypeCode &&
        evidence.scopeKey === scope.scopeKey &&
        evidence.contextHash === proof.contextHash &&
        typeof evidence.timeZone === 'string' &&
        evidence.timeZone.length > 0 &&
        !!evidence.values &&
        !!evidence.lists &&
        termsMatch &&
        Array.isArray(evidence.diagnostics) &&
        evidence.diagnostics.length <= 30 &&
        proof.diagnostics.length <= 30 &&
        proof.diagnostics.every(diagnostic) &&
        evidence.diagnostics.every(diagnostic) &&
        JSON.stringify(proof.diagnostics) ===
          JSON.stringify(evidence.diagnostics)
      );
    } catch {
      return false;
    }
  }
  private readLocal(): SavedNoticeSource | null {
    if (!this.scope || !this.actorId) return null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw || raw.length > 100000) return null;
      const value: SavedNoticeSource = JSON.parse(raw);
      if (
        !value ||
        typeof value.sourceJson !== 'string' ||
        new TextEncoder().encode(value.sourceJson).length >
          (this.catalog?.limits.sourceBytes ?? 32768)
      )
        return null;
      return value.contractIdentity ===
        this.contractIdentityFor(value.sourceJson) &&
        value.actorId === this.actorId &&
        value.scopeTypeCode === this.scope.scopeTypeCode &&
        value.scopeKey === this.scope.scopeKey &&
        typeof value.sourceJson === 'string' &&
        /^[a-f0-9]{64}$/.test(value.sourceHash) &&
        value.rendererCode === this.catalog?.rendererCode &&
        value.rendererVersion === this.catalog.rendererVersion
        ? value
        : null;
    } catch {
      return null;
    }
  }
  private current(generation: number, source: string, scope: string): boolean {
    return (
      !this.destroyed &&
      generation === this.generation &&
      source === this.sourceJson &&
      scope === this.scopeKey &&
      this.roles.accessProfile()?.actor.actorId === this.actorId &&
      this.canRead()
    );
  }
  private updateDirty(): void {
    this.state.dirty.set(this.dirty);
  }
  private async allowReplace(): Promise<boolean> {
    return (
      !this.dirty ||
      (await this.confirmation.confirm({
        heading: 'Replace notice composition edits?',
        message: 'Unsaved local composition changes will be lost.',
        confirmText: 'Replace edits',
        cancelText: 'Keep editing',
      }))
    );
  }
  private handleError(error: unknown): void {
    this.proof = null;
    this.comparisonProofs = null;
    if (
      error instanceof HttpErrorResponse &&
      [401, 403].includes(error.status)
    ) {
      this.clearProtected();
      this.error =
        'Notice proof access changed. Protected buffers were cleared. Refresh access before continuing.';
      return;
    }
    if (
      error instanceof HttpErrorResponse &&
      Array.isArray(error.error?.diagnostics)
    )
      this.diagnostics = error.error.diagnostics;
    this.error =
      error instanceof HttpErrorResponse && error.status === 400
        ? 'The renderer rejected this composition. Resolve the diagnostics; your source remains open.'
        : 'The notice proof request failed. Your source remains open; retry when the renderer is available.';
  }
  private clearBuffer(): void {
    this.clearAssets();
    this.closeMoreActions(false);
    this.cancel();
    this.history.reset();
    this.resetPageView();
    this.selectedBlockId = null;
    this.pageFocusBlockOnOpen = null;
    this.view = 'page';
    this.sourceJson = '';
    this.composition = null;
    this.saved = null;
    this.baseline = null;
    this.untouchedSource = null;
    this.diagnostics = [];
    this.notice = '';
    this.state.dirty.set(false);
  }
  private clearProtected(): void {
    this.catalogGeneration++;
    this.catalogRequest?.unsubscribe();
    this.loading = false;
    this.clearBuffer();
    this.catalog = null;
    this.scopeKey = '';
    this.accessLost = true;
  }
  @HostListener('window:beforeunload', ['$event']) protectEdits(
    event: BeforeUnloadEvent,
  ): void {
    if (this.state.isNavigationBlocked()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearProtected();
  }
}
